import { EyeOutlined } from '@ant-design/icons';
import {
    Button,
    Card,
    DatePicker,
    Descriptions,
    Drawer,
    Input,
    InputNumber,
    message,
    Select,
    Space,
    Spin,
    Table,
    Tag,
    theme,
    Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { TFunction } from 'i18next';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../contexts/ApiContext';
import {
    actionLabelKey,
    actionsForArea,
    areaLabelKey,
    areaOfAction,
    auditLogEntryId,
    auditValueRows,
    type AuditLogCatalogue,
    type AuditLogEntry,
    type AuditLogFilters,
    type AuditLogListItem,
    type AuditLogPagination,
    type AuditLogSource,
    type AuditValueRow,
    defaultAuditLogFilters,
    describeAuditEntity,
    formatAuditValue,
    hasAuditDetail,
    levelLabelKey,
    levelTagColor,
    shopTimeText,
    sourceLabelKey,
    sourceTagColor,
} from '../../domain/auditLog';
import { formatLeiWhole } from '../../domain/controlling';

const { Text } = Typography;

const DAY_FORMAT = 'YYYY-MM-DD';
const FIRST_PAGE: AuditLogPagination = { page: 1, pageSize: 20, total: 0, totalPages: 0 };

interface UserOption {
    id: number;
    username: string;
}

// The shop's clock, shown exactly as the backend answers it (`shopTimeText`).
const formatTime = (value: string | null): string => shopTimeText(value) ?? '—';

const actionLabel = (t: TFunction, action: string): string => t(actionLabelKey(action), action);

const renderSource = (t: TFunction, source: string) => (
    <Tag color={sourceTagColor(source)}>{t(sourceLabelKey(source), source)}</Tag>
);

const renderAction = (t: TFunction, record: AuditLogListItem) => {
    const levelColor = levelTagColor(record.logLevel);
    return (
        <Space size={4} wrap>
            <span>{actionLabel(t, record.action)}</span>
            {levelColor === undefined ? null : (
                <Tag color={levelColor}>{t(levelLabelKey(record.logLevel), record.logLevel)}</Tag>
            )}
        </Space>
    );
};

/** A legacy row carries its amount and note; the log's own rows carry a summary. */
const renderSummary = (record: AuditLogListItem) => {
    const extras = [
        record.amount === null ? null : formatLeiWhole(record.amount),
        record.reference,
    ].filter((part): part is string => part !== null && part !== '');
    return (
        <Space direction="vertical" size={0}>
            {record.summary === null ? null : <span>{record.summary}</span>}
            {extras.length === 0 ? null : <Text type="secondary">{extras.join(' · ')}</Text>}
        </Space>
    );
};

const buildColumns = (
    t: TFunction,
    onOpen: (record: AuditLogListItem) => void,
): ColumnsType<AuditLogListItem> => [
    {
        title: t('controlRoom.auditLog.columns.time', 'Time'),
        dataIndex: 'occurredAt',
        key: 'occurredAt',
        width: 170,
        render: (value: string | null) => formatTime(value),
    },
    {
        title: t('controlRoom.auditLog.columns.user', 'User'),
        dataIndex: 'username',
        key: 'username',
        width: 130,
        render: (username: string | null) =>
            username ?? t('controlRoom.auditLog.noUser', 'No user'),
    },
    {
        title: t('controlRoom.auditLog.columns.source', 'Source'),
        dataIndex: 'source',
        key: 'source',
        width: 120,
        render: (source: string) => renderSource(t, source),
    },
    {
        title: t('controlRoom.auditLog.columns.action', 'Operation'),
        dataIndex: 'action',
        key: 'action',
        width: 260,
        render: (_action: string, record: AuditLogListItem) => renderAction(t, record),
    },
    {
        title: t('controlRoom.auditLog.columns.entity', 'Concerns'),
        key: 'entity',
        width: 220,
        render: (_value: unknown, record: AuditLogListItem) => describeAuditEntity(record),
    },
    {
        title: t('controlRoom.auditLog.columns.summary', 'Description'),
        key: 'summary',
        render: (_value: unknown, record: AuditLogListItem) => renderSummary(record),
    },
    {
        key: 'open',
        width: 110,
        render: (_value: unknown, record: AuditLogListItem) =>
            hasAuditDetail(record) ? (
                <Button size="small" icon={<EyeOutlined />} onClick={() => onOpen(record)}>
                    {t('controlRoom.auditLog.detail.open', 'Details')}
                </Button>
            ) : null,
    },
];

const buildValueColumns = (t: TFunction): ColumnsType<AuditValueRow> => [
    {
        title: t('controlRoom.auditLog.detail.field', 'Field'),
        dataIndex: 'field',
        key: 'field',
        width: 200,
        render: (field: string, row: AuditValueRow) => (
            <Space size={4}>
                <Text strong={row.changed}>{field}</Text>
                {row.changed ? (
                    <Tag color="orange">{t('controlRoom.auditLog.detail.changed', 'Changed')}</Tag>
                ) : null}
            </Space>
        ),
    },
    {
        title: t('controlRoom.auditLog.detail.oldValue', 'Before'),
        dataIndex: 'before',
        key: 'before',
        render: (value: unknown) => <Text code>{formatAuditValue(value)}</Text>,
    },
    {
        title: t('controlRoom.auditLog.detail.newValue', 'After'),
        dataIndex: 'after',
        key: 'after',
        render: (value: unknown) => <Text code>{formatAuditValue(value)}</Text>,
    },
];

interface AuditLogDetailProps {
    entry: AuditLogEntry;
    t: TFunction;
}

const AuditLogDetail = ({ entry, t }: AuditLogDetailProps) => {
    // The theme's warning background, so a changed row stays readable in the dark theme too.
    const { token } = theme.useToken();
    const rows = auditValueRows(entry.oldValues, entry.newValues);
    const changedRowStyle = (row: AuditValueRow) => ({
        style: row.changed ? { backgroundColor: token.colorWarningBg } : {},
    });
    return (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label={t('controlRoom.auditLog.columns.time', 'Time')}>
                    {formatTime(entry.occurredAt)}
                </Descriptions.Item>
                <Descriptions.Item label={t('controlRoom.auditLog.columns.user', 'User')}>
                    {entry.username ?? t('controlRoom.auditLog.noUser', 'No user')}
                </Descriptions.Item>
                <Descriptions.Item label={t('controlRoom.auditLog.columns.action', 'Operation')}>
                    {actionLabel(t, entry.action)} <Text type="secondary">({entry.action})</Text>
                </Descriptions.Item>
                <Descriptions.Item label={t('controlRoom.auditLog.columns.entity', 'Concerns')}>
                    {describeAuditEntity(entry)}
                </Descriptions.Item>
                <Descriptions.Item label={t('controlRoom.auditLog.columns.source', 'Source')}>
                    {renderSource(t, entry.source)}
                </Descriptions.Item>
                <Descriptions.Item label={t('controlRoom.auditLog.detail.level', 'Level')}>
                    {t(levelLabelKey(entry.logLevel), entry.logLevel)}
                </Descriptions.Item>
                <Descriptions.Item label={t('controlRoom.auditLog.columns.summary', 'Description')}>
                    {entry.summary ?? '—'}
                </Descriptions.Item>
                <Descriptions.Item label={t('controlRoom.auditLog.detail.ipAddress', 'IP address')}>
                    {entry.ipAddress ?? '—'}
                </Descriptions.Item>
                <Descriptions.Item label={t('controlRoom.auditLog.detail.userAgent', 'Browser')}>
                    {entry.userAgent ?? '—'}
                </Descriptions.Item>
                {entry.bridgeEventId === null ? null : (
                    <Descriptions.Item
                        label={t('controlRoom.auditLog.detail.bridgeEvent', 'Delphi event')}
                    >
                        {entry.bridgeEventId}
                    </Descriptions.Item>
                )}
            </Descriptions>
            {rows.length === 0 ? (
                <Text type="secondary">
                    {t('controlRoom.auditLog.detail.noValues', 'This entry records no values.')}
                </Text>
            ) : (
                <Table
                    size="small"
                    rowKey="field"
                    dataSource={rows}
                    columns={buildValueColumns(t)}
                    pagination={false}
                    onRow={changedRowStyle}
                    data-testid="audit-log-values"
                />
            )}
        </Space>
    );
};

const toRange = (filters: AuditLogFilters): [dayjs.Dayjs, dayjs.Dayjs] | null =>
    filters.fromDate === undefined || filters.toDate === undefined
        ? null
        : [dayjs(filters.fromDate), dayjs(filters.toDate)];

interface AuditLogPanelProps {
    /** How many times the tab has been opened: each reopening reloads the list, as before. */
    openings?: number;
}

/**
 * "Felhasználói műveletek": the operation log - who did what, when and from where, with the old
 * and new values of every change (audit plan F4). For administrators only (D2).
 */
const AuditLogPanel = ({ openings = 1 }: AuditLogPanelProps) => {
    const { t } = useTranslation();
    const { controlRoom, makeRequest } = useApi();
    const [initialFilters] = useState<AuditLogFilters>(() => defaultAuditLogFilters(new Date()));
    const [draft, setDraft] = useState<AuditLogFilters>(initialFilters);
    const [applied, setApplied] = useState<AuditLogFilters>(initialFilters);
    const [items, setItems] = useState<AuditLogListItem[]>([]);
    const [pagination, setPagination] = useState<AuditLogPagination>(FIRST_PAGE);
    const [loading, setLoading] = useState(false);
    const [catalogue, setCatalogue] = useState<AuditLogCatalogue | null>(null);
    const [users, setUsers] = useState<UserOption[]>([]);
    const [entry, setEntry] = useState<AuditLogEntry | null>(null);
    const [entryLoading, setEntryLoading] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    // Only the latest request may fill the table or the drawer: a wide range answers after a
    // narrow one asked later, and would show rows for filters no longer applied.
    const pageSeq = useRef(0);
    const entrySeq = useRef(0);
    // What the tab has loaded. The API object is rebuilt whenever the session token is refreshed,
    // which reruns the first-load effect: it then fetches only what has not arrived yet - a
    // catalogue or user list that failed on an expired token - and asks for the first page again
    // only when that request failed and the operator has not asked for a page since: never twice
    // at once (each reading is logged), never over the page and filters the operator chose.
    const catalogueLoaded = useRef(false);
    const usersLoaded = useRef(false);
    const firstPageAsked = useRef(false);
    const operatorAsked = useRef(false);
    const openingsSeen = useRef(openings);

    /** Loads a page; true unless it failed. The filters count as applied once their rows show. */
    const loadPage = useCallback(
        async (
            filters: AuditLogFilters,
            page: number,
            pageSize: number,
            upToId?: number | null,
        ): Promise<boolean> => {
            const seq = ++pageSeq.current;
            setLoading(true);
            try {
                const response = await controlRoom.getAuditLog(filters, page, pageSize, upToId);
                if (seq !== pageSeq.current) return true;
                if (!response.success) {
                    // The table keeps the rows and filters it shows, so a page click goes on
                    // with them rather than with filters that never loaded.
                    message.error(
                        t('controlRoom.auditLog.loadError', 'Failed to load the operation log'),
                    );
                    return false;
                }
                setApplied(filters);
                setItems(response.data ?? []);
                setPagination(response.pagination ?? { ...FIRST_PAGE, page, pageSize });
                return true;
            } finally {
                if (seq === pageSeq.current) setLoading(false);
            }
        },
        [controlRoom, t],
    );

    /** A page the operator asked for: from then on the first-load effect leaves the table alone. */
    const loadChosenPage = (
        filters: AuditLogFilters,
        page: number,
        pageSize: number,
        upToId?: number | null,
    ) => {
        operatorAsked.current = true;
        void loadPage(filters, page, pageSize, upToId);
    };

    useEffect(() => {
        if (!catalogueLoaded.current) {
            void controlRoom.getAuditLogCatalogue().then(response => {
                if (response.success && response.data) {
                    catalogueLoaded.current = true;
                    setCatalogue(response.data);
                }
            });
        }
        if (!usersLoaded.current) {
            void makeRequest<UserOption[]>('/cash-drawer/users').then(response => {
                if (response.success && response.data) {
                    usersLoaded.current = true;
                    setUsers(response.data);
                }
            });
        }
        // The first page: later pages and filters are the operator's.
        if (!firstPageAsked.current && !operatorAsked.current) {
            firstPageAsked.current = true;
            void loadPage(initialFilters, FIRST_PAGE.page, FIRST_PAGE.pageSize).then(shown => {
                firstPageAsked.current = shown;
            });
        }
    }, [controlRoom, makeRequest, loadPage, initialFilters]);

    // Coming back to the tab shows what happened meanwhile: the applied filters, read afresh.
    // Only a new opening reloads; the filters and page size are read as they stand then.
    const shownList = useRef({ filters: applied, pageSize: pagination.pageSize });
    shownList.current = { filters: applied, pageSize: pagination.pageSize };
    useEffect(() => {
        if (openings === openingsSeen.current) {
            return;
        }
        openingsSeen.current = openings;
        operatorAsked.current = true;
        const { filters, pageSize } = shownList.current;
        void loadPage(filters, FIRST_PAGE.page, pageSize);
    }, [openings, loadPage]);

    const openEntry = useCallback(
        async (record: AuditLogListItem) => {
            const id = auditLogEntryId(record.id);
            if (id === null) {
                return;
            }
            const seq = ++entrySeq.current;
            setDrawerOpen(true);
            setEntry(null);
            setEntryLoading(true);
            try {
                const response = await controlRoom.getAuditLogEntry(id);
                if (seq !== entrySeq.current) return;
                if (response.success && response.data) {
                    setEntry(response.data);
                } else {
                    message.error(
                        t('controlRoom.auditLog.detailLoadError', 'Failed to load the log entry'),
                    );
                    setDrawerOpen(false);
                }
            } finally {
                if (seq === entrySeq.current) setEntryLoading(false);
            }
        },
        [controlRoom, t],
    );

    // Closing drops the answer still on its way: a late failure would toast for a closed drawer.
    const closeEntry = useCallback(() => {
        entrySeq.current++;
        setDrawerOpen(false);
        setEntryLoading(false);
    }, []);

    const columns = useMemo(
        () => buildColumns(t, record => void openEntry(record)),
        [t, openEntry],
    );

    const areaOptions = useMemo(
        () =>
            (catalogue?.areas ?? []).map(({ area }) => ({
                value: area,
                label: t(areaLabelKey(area), area),
            })),
        [catalogue, t],
    );
    const actionOptions = useMemo(
        () =>
            actionsForArea(catalogue, draft.area).map(action => ({
                value: action,
                label: actionLabel(t, action),
            })),
        [catalogue, draft.area, t],
    );
    const sourceOptions = useMemo(
        () =>
            (catalogue?.sources ?? []).map(source => ({
                value: source,
                label: t(sourceLabelKey(source), source),
            })),
        [catalogue, t],
    );

    const updateDraft = (changes: Partial<AuditLogFilters>) =>
        setDraft(current => ({ ...current, ...changes }));

    // New filters are a new list: it is read up to the newest row there is then.
    const applyFilters = (filters: AuditLogFilters) =>
        loadChosenPage(filters, FIRST_PAGE.page, pagination.pageSize);

    const resetFilters = () => {
        const filters = defaultAuditLogFilters(new Date());
        setDraft(filters);
        applyFilters(filters);
    };

    return (
        <Card size="small">
            <Space wrap style={{ marginBottom: 16 }} data-testid="audit-log-filters">
                <DatePicker.RangePicker
                    value={toRange(draft)}
                    format={DAY_FORMAT}
                    onChange={range =>
                        updateDraft({
                            fromDate: range?.[0]?.format(DAY_FORMAT),
                            toDate: range?.[1]?.format(DAY_FORMAT),
                        })
                    }
                />
                <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder={t('controlRoom.auditLog.filters.allUsers', 'All users')}
                    value={draft.userId}
                    onChange={(userId?: number) => updateDraft({ userId })}
                    options={users.map(user => ({ value: user.id, label: user.username }))}
                    style={{ minWidth: 170 }}
                    data-testid="audit-log-user"
                />
                <Select
                    allowClear
                    placeholder={t('controlRoom.auditLog.filters.allAreas', 'All areas')}
                    value={draft.area}
                    onChange={(area?: string) => updateDraft({ area, action: undefined })}
                    options={areaOptions}
                    style={{ minWidth: 190 }}
                    data-testid="audit-log-area"
                />
                <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder={t('controlRoom.auditLog.filters.allActions', 'All operations')}
                    value={draft.action}
                    onChange={(action?: string) =>
                        updateDraft({
                            action,
                            area:
                                action === undefined ? draft.area : areaOfAction(catalogue, action),
                        })
                    }
                    options={actionOptions}
                    style={{ minWidth: 240 }}
                    data-testid="audit-log-action"
                />
                <Select
                    allowClear
                    placeholder={t('controlRoom.auditLog.filters.allSources', 'All sources')}
                    value={draft.source}
                    onChange={(source?: AuditLogSource) => updateDraft({ source })}
                    options={sourceOptions}
                    style={{ minWidth: 150 }}
                    data-testid="audit-log-source"
                />
                <Input
                    allowClear
                    placeholder={t('controlRoom.auditLog.filters.contract', 'Contract')}
                    value={draft.contract ?? ''}
                    onChange={event => updateDraft({ contract: event.target.value })}
                    style={{ width: 150 }}
                    data-testid="audit-log-contract"
                />
                <InputNumber
                    min={1}
                    precision={0}
                    placeholder={t('controlRoom.auditLog.filters.client', 'Client ID')}
                    value={draft.clientId ?? null}
                    onChange={clientId => updateDraft({ clientId: clientId ?? undefined })}
                    style={{ width: 140 }}
                    data-testid="audit-log-client"
                />
                <Button type="primary" onClick={() => applyFilters(draft)}>
                    {t('controlRoom.auditLog.filters.filter', 'Filter')}
                </Button>
                <Button onClick={resetFilters}>
                    {t('controlRoom.auditLog.filters.reset', 'Reset')}
                </Button>
            </Space>
            <Table
                dataSource={items}
                columns={columns}
                rowKey="id"
                loading={loading}
                size="small"
                scroll={{ x: 1100 }}
                locale={{
                    emptyText: t('controlRoom.auditLog.empty', 'No operations match the filters'),
                }}
                pagination={{
                    current: pagination.page,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    showSizeChanger: true,
                    pageSizeOptions: [20, 50, 100],
                    // The same list: read up to the row it was opened with.
                    onChange: (page, pageSize) =>
                        loadChosenPage(applied, page, pageSize, pagination.upToId),
                }}
                data-testid="audit-log-table"
            />
            <Drawer
                title={t('controlRoom.auditLog.detail.title', 'Log entry')}
                width={760}
                open={drawerOpen}
                onClose={closeEntry}
                destroyOnHidden
            >
                {entryLoading || entry === null ? <Spin /> : <AuditLogDetail entry={entry} t={t} />}
            </Drawer>
        </Card>
    );
};

export default AuditLogPanel;
